import React, { useEffect, useState, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import './SearchBarWithSuggestions.scss';
import FormattedSuggestion from '../formattedSuggestion/FormattedSuggestion';
import { useSearchData } from 'app/services/search-api';
import { SearchOption, SuggestResponse } from 'generated/search-backend';
import useStore, { IStore } from 'app/store';
import voiceIcon from 'app/static/icons/microVoice.svg';
import { useSpeechToTextToken } from 'app/services/search-api';

import { isUser } from 'app/utils/authUtilities';
import { keyPress } from 'app/components/helpers';

import * as speechSdk from 'microsoft-cognitiveservices-speech-sdk';
import { UserDto } from 'generated/request-backend';
import { appInsightsService } from 'app/utils/constants';
import { isQuotedPhrase, SEARCH_PLACEHOLDERS } from 'app/utils/searchUtilities';
import { debounce } from 'lodash';
export interface SearchResult {
    name: string;
    link: string;
}

const SearchBarWithSuggestions: React.FC = () => {
    const userDetails: UserDto = useStore((state: IStore) => state.userDetails as UserDto);

    const [isSearchResultShown, setIsSearchResultShown] = useState(false);

    //location
    let history = useHistory();

    const location = useLocation();
    const { pathname } = location;

    const [searchText, setSearchText] = useState('');
    const [searchResult, setSearchResult] = useState<SuggestResponse[]>([]);

    const setUserSearchValue = useStore((state: IStore) => state.setUserSearchValue);

    const { mutate: searchData, data: searchResultData, isSuccess: isSuccessSearch } = useSearchData();

    const [isSearchFocused, setIsSearchFocused] = useState(false);

    const { refetch: getSpeechToTextToken } = useSpeechToTextToken();
    const [isRecording, setIsRecording] = useState(false);
    const [recognizer, setRecognizer] = useState(null);
    const [isSearchInProgress, setIsSearchInProgress] = useState(false);

    //voice to text
    const sttFromMic = async (action: Boolean) => {
        if (action) {
            try {
                let token = JSON.parse(sessionStorage.getItem('speech-token'));
                if (!(token && token.Token)) {
                    await getSpeechToTextToken();
                    token = JSON.parse(sessionStorage.getItem('speech-token'));
                }
                const speechConfig = speechSdk.SpeechConfig.fromAuthorizationToken(token?.Token ?? '', token?.Region ?? '');
                speechConfig.speechRecognitionLanguage = 'en-US';
                speechConfig.setServiceProperty('punctuation', 'explicit', speechSdk.ServicePropertyChannel.UriQueryParameter);
                const audioConfig = speechSdk.AudioConfig.fromDefaultMicrophoneInput();

                let newRecognizer = new speechSdk.SpeechRecognizer(speechConfig, audioConfig);
                setRecognizer(newRecognizer);
                setIsRecording(true);

                newRecognizer.recognized = function (s: speechSdk.Recognizer, e: speechSdk.SpeechRecognitionEventArgs) {
                    let displayText;

                    if (e.result.text) {
                        displayText = `${e.result.text}`;
                        displayText = displayText.replace('SKP ', 'SKP-');
                        //displayText = displayText.substring(0, displayText.length - 1);

                        setSearchText(displayText);
                        setUserSearchValue(displayText);
                        showResults();
                        setIsRecording(false);
                        searchFieldRef.current.focus();
                        searchData({
                            term: displayText,
                            searchOption: SearchOption.HelpRequest,
                            isAdminSearch:
                                !isUser(userDetails?.userRoles) &&
                                (pathname.includes('manage-requests') ||
                                    (!isUser(userDetails?.userRoles) && pathname === '/') ||
                                    pathname.includes('report') ||
                                    pathname.includes('request-detail') ||
                                    pathname.includes('visual-flow')),
                            isParentChildSearch: false
                        });

                        newRecognizer.stopContinuousRecognitionAsync(
                            () => {},
                            (e: string) => {}
                        );
                    }
                };

                newRecognizer.startContinuousRecognitionAsync(() => {
                    setTimeout(() => {
                        newRecognizer.stopContinuousRecognitionAsync(
                            () => {},
                            (e: string) => {}
                        );
                        setIsRecording(false);
                    }, 20000);
                });
            } catch (e) {
                console.error('Speech recognition failed to initialize:', e);
                setIsRecording(false);
            }
        }

        if (!action) {
            recognizer.stopContinuousRecognitionAsync(
                (result: string) => {},
                (e: string) => {}
            );
        }
    };

    const voiceStartClick = () => {
        sttFromMic(true);
    };

    useEffect(() => {
        if (isSuccessSearch) {
            setSearchResult(searchResultData);
        }
    }, [isSuccessSearch]);

    const onFocus = () => {
        showResults();
        appInsightsService.logEvent("SearchBarFocus: focused on search bar");
    };

    const handleBlur = () => {
        setIsSearchInProgress(false);
        appInsightsService.logEvent("SearchBarBlur: blurred from search bar");
    };

    const showResults = () => {
        setIsSearchFocused(true);
        if (searchText.length >= 3) setIsSearchResultShown(true);
        else setIsSearchResultShown(false);
    };

    // add debounce to avoid multiple search requests in the backend as user is typing
    const debounceSearch = React.useCallback(debounce(searchData, 300), []);
    
    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);

        setUserSearchValue(event.target.value);
        if (event.target.value.trim())
            debounceSearch({
                term: event.target.value,
                searchOption: SearchOption.HelpRequest,
                isAdminSearch:
                    !isUser(userDetails?.userRoles) &&
                    (pathname.includes('manage-requests') ||
                        (!isUser(userDetails?.userRoles) && pathname === '/') ||
                        pathname.includes('report') ||
                        pathname.includes('request-detail') ||
                        pathname.includes('visual-flow')),
                isParentChildSearch: false
            });

        if (searchText.length >= 3 || event.target.value.length >= 3) setIsSearchResultShown(true);
        else setIsSearchResultShown(false);
    };

    const onKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
        // log the search initiation event
        // log only one time when the search is initiated
        if(!isSearchInProgress){
            setIsSearchInProgress(true);
            appInsightsService.logEvent("SearchInitiated: initiated a search");
        }
        if (event.code === 'Enter' || event.code === 'NumpadEnter') {
            const submittedValue = searchText;
            setSearchText('');
            history.push('/search-results');
            setIsSearchResultShown(false);
            setSearchResult([]);
            setIsSearchInProgress(false);
            appInsightsService.logEvent("SearchSubmission: Submitted search", {
                phraseMatch: String(isQuotedPhrase(submittedValue))
            });
        }
    };
    const handleSuggestionClose = () => {
        if (searchText.length === 0) setIsSearchFocused(false);
        setIsSearchResultShown(false);

        setSearchText('');
    };

    const searchContainer = useRef<HTMLDivElement>(null);
    useSearchSuggestionClose(searchContainer, handleSuggestionClose);
    const searchFieldRef = useRef(null);
    return (
        <>
            <div className="search-box-top" role="searchbox" aria-label="search-requests-field" ref={searchContainer}>
                {!isRecording ? (
                    <img
                        tabIndex={0}
                        src={voiceIcon}
                        alt="Voice"
                        className="voice-icon"
                        onKeyPress={(e) => keyPress(e, voiceStartClick)}
                        onClick={() => {
                            voiceStartClick();
                        }}
                    />
                ) : (
                    ''
                )}
                {isRecording ? (
                    <div
                        className="recording-row recording-position"
                        onKeyPress={(e) =>
                            keyPress(e, () => {
                                setIsRecording(false);
                                sttFromMic(false);
                            })
                        }
                        tabIndex={0}
                        onClick={() => {
                            setIsRecording(false);
                            sttFromMic(false);
                        }}>
                        <div className="first-line"></div>
                        <div className="second-line"></div>
                        <div className="third-line"></div>
                        <div className="fourth-line"></div>
                        <div className="fifth-line"></div>
                    </div>
                ) : (
                    ''
                )}
                <input
                    tabIndex={0}
                    className={`search-input ${
                        isSearchResultShown ? 'search-input-focus' : searchResult?.length > 0 ? 'show-suggestions ' : ''
                    } ${searchText?.length > 3 ? 'search-input-grown' : ''}  ${
                        isSearchFocused ? 'search-animation' : 'search-shrink-animation'
                    }`}
                    onChange={(event) => onChange(event)}
                    onFocus={() => onFocus()}
                    onBlur={handleBlur}
                    type="text"
                    onKeyUp={(event) => onKeyUp(event)}
                    value={searchText}
                    data-testid="search-text"
                    role="searchbox"
                    ref={searchFieldRef}
                    placeholder={isSearchFocused ? SEARCH_PLACEHOLDERS.headerExpanded : 'Search'}
                />
                {isSearchResultShown ? (
                    <div className={searchResult?.length > 0 ? 'suggestions' : 'search-box-suggestions'}>
                        <ul>
                            {isSuccessSearch &&
                                searchResult?.length > 0 &&
                                searchResult.map((item: SuggestResponse, index) => {
                                    return (
                                        <div key={index}>
                                            <FormattedSuggestion
                                                name={item.document.title}
                                                UnifiedTrackingId={item.document.unifiedTrackingId}
                                                inputString={searchText}
                                                setSearchText={setSearchText}
                                                handleSuggestionClose={handleSuggestionClose}
                                            />
                                        </div>
                                    );
                                })}
                        </ul>
                    </div>
                ) : (
                    <div></div>
                )}
            </div>
        </>
    );
};

const useSearchSuggestionClose = (ref: React.MutableRefObject<HTMLDivElement>, handleSuggestionClose: Function) => {
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                handleSuggestionClose();
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handleSuggestionClose, ref]);
};
export default SearchBarWithSuggestions;
